import type { ReactNode } from "react";
import type { AskResult, LinkedEntity, LinkedRelation } from "@nl2sparql/core";
import { Panel } from "../../components/Panel.tsx";

export interface DisambiguationPick {
  kind: "entity" | "property";
  iri: string;
  label: string;
}

function Chip({
  label,
  id,
  href,
  tone,
  onClick,
  title,
}: {
  label: string;
  id?: string;
  href?: string;
  tone: "accent" | "muted";
  onClick?: () => void;
  title?: string;
}) {
  const style =
    tone === "accent"
      ? { borderColor: "var(--nl2s-accent)", color: "var(--nl2s-accent)", background: "var(--nl2s-accent-soft)" }
      : { borderColor: "var(--nl2s-border)", color: "var(--nl2s-text-muted)", background: "transparent" };
  const inner = (
    <>
      <span>{label}</span>
      {id ? <span className="font-mono text-[10px] opacity-70">{id}</span> : null}
    </>
  );
  const cls = "inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs no-underline cursor-pointer";
  if (onClick) {
    return (
      <button type="button" onClick={onClick} title={title} className={cls} style={style}>
        {inner}
      </button>
    );
  }
  if (href) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className={cls} style={style} title={title}>
        {inner}
      </a>
    );
  }
  return (
    <span className={cls} style={style}>
      {inner}
    </span>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
      <span className="w-20 shrink-0 text-xs font-medium" style={{ color: "var(--nl2s-text-muted)" }}>
        {label}
      </span>
      <span className="flex flex-wrap items-center gap-1.5">{children}</span>
    </div>
  );
}

function EntityRow({ e, onPick }: { e: LinkedEntity; onPick?: (p: DisambiguationPick) => void }) {
  return (
    <Row label={`“${e.mention}”`}>
      {e.chosen ? <Chip label={e.chosen.label} id={e.chosen.id} href={e.chosen.iri} tone="accent" title="Open" /> : null}
      {e.alternatives
        .filter((c) => c.iri !== e.chosen?.iri)
        .slice(0, 3)
        .map((c) => (
          <Chip
            key={c.iri}
            label={c.label}
            id={c.id}
            tone="muted"
            title="Use this entity instead"
            {...(onPick ? { onClick: () => onPick({ kind: "entity", iri: c.iri, label: c.label }) } : { href: c.iri })}
          />
        ))}
    </Row>
  );
}

function RelationRow({ r, onPick }: { r: LinkedRelation; onPick?: (p: DisambiguationPick) => void }) {
  return (
    <Row label={`“${r.phrase}”`}>
      {r.chosen ? (
        <Chip label={r.chosen.label} id={r.chosen.id} href={r.chosen.entityIri ?? r.chosen.iri} tone="accent" title="Open" />
      ) : null}
      {r.alternatives
        .filter((c) => c.iri !== r.chosen?.iri)
        .slice(0, 3)
        .map((c) => (
          <Chip
            key={c.iri}
            label={c.label}
            id={c.id}
            tone="muted"
            title="Use this property instead"
            {...(onPick ? { onClick: () => onPick({ kind: "property", iri: c.iri, label: c.label }) } : { href: c.entityIri ?? c.iri })}
          />
        ))}
    </Row>
  );
}

export function ExplainPanel({
  result,
  onPick,
}: {
  result: AskResult;
  onPick?: (p: DisambiguationPick) => void;
}) {
  const t = result.trace;
  if (!t) return null;
  return (
    <Panel title="Explain" subtitle="How the question was understood — click an alternative to re-run">
      <div className="space-y-3">
        <Row label="Template">
          <span className="font-mono text-xs" style={{ color: "var(--nl2s-text)" }}>
            {t.template}
          </span>
          <span
            className="rounded px-1.5 py-0.5 text-[10px] font-medium"
            style={{ background: "var(--nl2s-surface-raised)", color: "var(--nl2s-text-muted)" }}
          >
            {t.parser}
          </span>
        </Row>

        {t.entities.map((e, i) => (
          <EntityRow key={i} e={e} {...(onPick ? { onPick } : {})} />
        ))}
        {t.relations.map((r, i) => (
          <RelationRow key={i} r={r} {...(onPick ? { onPick } : {})} />
        ))}

        {t.classConstraint ? (
          <Row label="Type">
            <Chip
              label={t.classConstraint.label}
              id={t.classConstraint.qid}
              href={`http://www.wikidata.org/entity/${t.classConstraint.qid}`}
              tone="accent"
            />
          </Row>
        ) : null}

        {t.superlative ? (
          <Row label="Order by">
            <span className="text-xs" style={{ color: "var(--nl2s-text)" }}>
              {t.superlative.label} ({t.superlative.descending ? "desc" : "asc"})
            </span>
          </Row>
        ) : null}

        {result.plan ? (
          <div className="mt-2 border-t pt-3" style={{ borderColor: "var(--nl2s-border)" }}>
            <Row label="Source">
              <span className="text-xs" style={{ color: "var(--nl2s-text-muted)" }}>
                {result.plan.rationale}
              </span>
              {result.plan.federated ? (
                <span
                  className="rounded px-1.5 py-0.5 text-[10px] font-semibold"
                  style={{ background: "var(--nl2s-accent-soft)", color: "var(--nl2s-accent)" }}
                >
                  federated
                </span>
              ) : null}
            </Row>
          </div>
        ) : null}

        {t.notes.length > 0 ? (
          <ul className="mt-1 list-disc pl-5 text-xs" style={{ color: "var(--nl2s-text-muted)" }}>
            {t.notes.map((n, i) => (
              <li key={i}>{n}</li>
            ))}
          </ul>
        ) : null}
      </div>
    </Panel>
  );
}
